import {
	Component,
	OnInit,
	Input,
	Output,
	EventEmitter,
	SimpleChanges,
	OnChanges,
	OnDestroy
} from '@angular/core';
import { InvoicesDataProvider } from '@services/invoice/invoices-data-service/invoices.data.service';
import { MessageService } from '@services/helpers/message-service/message.service';
import { Invoice, Payment } from '@models/invoice.model';
import { ValidationProvider } from '@services/helpers/validation.service';
import { Subject } from 'rxjs/Subject';
import { finalize, takeUntil } from 'rxjs/operators';
import { GenericModalContent, InvoicePayment } from '@models/models';
import { SNModal } from '@web-services/modal/snModal.interfaces';
import { SNModalService } from '@web-services/modal/snModal.service';
import { WriteOffModalComponent } from '@web-components/payments/modals/write-off-modal/write-off-modal';

@Component({
	selector: 'invoice-view',
	template: require('./invoice-view.html'),
	styles: [require('./invoice-view.scss')]
})
export class InvoiceViewComponent implements OnInit, OnChanges, OnDestroy {
	@Input('invoiceId') invoiceId: number;
	@Input() sendReceiptSpinner: boolean;
	@Output() sendMail: EventEmitter<any> = new EventEmitter();
	@Output() getContactEmail: EventEmitter<string> = new EventEmitter();
	invoice: Invoice;
	isLeadView: boolean;
	userCurrencySymbol: string;
	selectedPayment: InvoicePayment;
	paidOn: Date;
	spinners;
	writeOffModalContent: GenericModalContent;

	private unsubscribe: Subject<void> = new Subject();

	constructor(
		private invoiceDataService: InvoicesDataProvider,
		private messageService: MessageService,
		private validationService: ValidationProvider,
		private modalService: SNModalService
	) {}

	ngOnInit(): void {
		this.configureCommunicationAdapter();
		this.getInvoice();
	}

	ngOnChanges(change: SimpleChanges): void {
		// We need to track infinite spinner variable from old Angular. Could be removed after upgrading SendReceipt modal.
		if (
			!change.sendReceiptSpinner.isFirstChange() &&
			this.selectedPayment &&
			this.spinners.sendReceiptSpinner[this.selectedPayment.id]
		) {
			this.spinners.sendReceiptSpinner[this.selectedPayment.id] =
				change.sendReceiptSpinner.currentValue;
		}
	}

	ngOnDestroy(): void {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	showWriteOff(payment: InvoicePayment): void {
		this.selectPayment(payment);
		this.prepareWriteOffData(payment);
		this.showWriteOffModal();
	}

	writeOff(): void {
		this.getInvoice();
	}

	undoOff(payment: InvoicePayment): void {
		this.selectPayment(payment);
		this.spinners.undoWriteOffSpinner[payment.id] = true;
		this.invoiceDataService
			.undoWriteOff(payment.id)
			.pipe(
				takeUntil(this.unsubscribe),
				finalize((): void => this.discardSpinners())
			)
			.subscribe(
				(): void => this.getInvoice(),
				({ error }): void => {
					const { errorMessage, errorTitle } = error;
					this.messageService.showErrorFromBackend(errorTitle, errorMessage);
				}
			);
	}

	payOnline(): void {
		const newWindow = window.open('', '_blank');

		this.invoiceDataService
			.getInvoicePreviewURL(this.invoiceId)
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((result): void => {
				const clientLink = result.link;
				const base = window.location.origin;
				newWindow.location.href = `${base}/clients/invoice/pay/${clientLink}/${
					this.invoiceId
				}`;
			});
	}

	sendReceipt(sendData): void {
		if (sendData.isReceipt) {
			this.spinners.sendReceiptSpinner[sendData.data.id] = true;
			this.selectPayment(sendData.data as InvoicePayment);
		}

		this.sendMail.emit(sendData);
	}

	setPaid(payment: InvoicePayment): void {
		this.selectPayment(payment);
		if (
			payment.errorMessage ===
			"StudioNinja doesn't support transactions with Credit Notes from QuickBooks"
		) {
			this.messageService.showToaster('e_14', 'qb');
		} else if (
			payment.errorMessage ===
			'One of the list elements assigned to this transaction has been deleted'
		) {
			this.messageService.showToaster('e_11', 'qb');
		} else {
			this.selectedPayment = payment;
			const setPaidModal = $('#markAsPayedModal');

			setPaidModal.modal('show').on('shown.bs.modal', () => {
				this.configurePreventCloseModal(setPaidModal);
			});
		}
	}

	onRecordPaymentClose(ev: boolean): void {
		if (ev) {
			this.getInvoice();
		}
	}

	setUnpaid(payment: InvoicePayment): void {
		this.selectPayment(payment);
		this.spinners.markUnpaidSpinner[payment.id] = true;
		this.invoiceDataService
			.setUnpaid(payment.id)
			.pipe(
				takeUntil(this.unsubscribe),
				finalize((): void => this.discardSpinners())
			)
			.subscribe(
				(result): void => this.successSetUnpaid(result),
				error => this.errorSetUnpaid
			);
	}

	getInvoice(): void {
		this.invoiceDataService
			.getInvoiceV1(this.invoiceId)
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(
				(data: Invoice): void => this.successGetInvoice(data),
				(error): void => this.messageService.showDefaultErrorMessage(error)
			);
	}

	private configurePreventCloseModal(modal): void {
		const input = modal.find('input');
		modal.find('input.ws-number').prop('autocomplete', 'off');

		this.validationService.isModalClosePrevented(input);
	}

	private successGetInvoice(invoice: Invoice): void {
		this.invoice = invoice;
		this.isLeadView = invoice.job.inLead;
		this.userCurrencySymbol = invoice.summary.totalWithCurrency.currencySymbol;
		this.getContactEmail.emit(invoice.job.primaryContact.email);
		this.prepareSpinners();
	}

	private successSetUnpaid(resp): void {
		if (resp && resp.data && resp.data.errorMessage) {
			this.messageService.showDefaultErrorMessage(resp);
		} else {
			this.messageService.showToaster('s_3', 'payments');
		}
		this.getInvoice();
	}

	private errorSetUnpaid({ status, data }): void {
		if (data.errorCode) {
			this.handleModernErrorMessages(data);
			return;
		}

		const errorMessage = data.errorMessage;
		let errorCode, errorPlace, value;

		if (status >= 400 && status < 500 && errorMessage) {
			if (
				errorMessage.includes(
					'This payment cannot be marked as unpaid because it was overpaid in Xero.'
				)
			) {
				errorCode = 'e_11';
				errorPlace = 'xero';
			} else {
				errorCode = 'e_2';
				errorPlace = 'general';
				value = errorMessage;
			}
		} else {
			errorCode = 'e_0';
			errorPlace = 'general';
		}
		this.messageService.showToaster(errorCode, errorPlace, value);
	}

	private prepareSpinners(): void {
		this.spinners = {
			sendReceiptSpinner: {},
			markUnpaidSpinner: {},
			undoWriteOffSpinner: {}
		};

		this.invoice.payments.map((e: Payment): void => {
			this.spinners.sendReceiptSpinner[e.id] = this.spinners.markUnpaidSpinner[
				e.id
			] = this.spinners.undoWriteOffSpinner[e.id] = false;
		});
	}

	private selectPayment(payment: InvoicePayment): void {
		this.selectedPayment = payment;
	}

	private discardSpinners(): void {
		this.spinners.sendReceiptSpinner[
			this.selectedPayment.id
		] = this.spinners.markUnpaidSpinner[
			this.selectedPayment.id
		] = this.spinners.undoWriteOffSpinner[this.selectedPayment.id] = false;
	}

	private prepareWriteOffData(payment: InvoicePayment): void {
		this.writeOffModalContent = {
			title: 'Write off',
			description: 'Are you sure?',
			buttons: [
				{
					color: 'danger',
					size: 'lg',
					text: 'Write off payment',
					action: 'WRITE_OFF'
				},
				{
					color: 'default',
					size: 'lg',
					text: 'Cancel',
					action: 'DISCARD'
				}
			]
		};
		if (payment.xeroStatus === 'COPIED_TO_XERO') {
			this.writeOffModalContent.description =
				'Writing off this invoice in Studio Ninja will add a credit note to this invoice in Xero. Once written off, you can only undo by removing the credit note in Xero. Are you sure?';
			this.writeOffModalContent.buttons[0].text = 'Yes';
			this.writeOffModalContent.buttons[1].text = 'No';
		}
	}

	private showWriteOffModal(): void {
		const modal = this.modalService.openModal(WriteOffModalComponent);

		modal.componentInstance.paymentId = this.selectedPayment.id;
		modal.componentInstance.modalContent = this.writeOffModalContent;

		modal.result.then((): void => this.writeOff());
	}

	private handleModernErrorMessages({ errorTitle, errorMessage }): void {
		this.messageService.showErrorFromBackend(errorTitle, errorMessage);
	}

	private configureCommunicationAdapter(): void {
		window.snCommunicationAdapter.registerMethod(
			'getInvoice',
			this.getInvoice.bind(this)
		);
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=oom9nsliule!./web/modern/components/invoices/invoice-view/invoice-view.ts